import { useState, useCallback } from 'react';
import { MESSAGE } from '../../../constants/message';
import { ACTION_VALUES, DEFAULTS } from '../constants';
import { buildWeeklyGLErrorMessage } from '../utils/glPostingMethods';

export const useGLPostingModals = ({ validateForApproval, onApprovalSuccess, createPosting, buildPostingData }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [failureMessage, setFailureMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState(DEFAULTS.EMPTY_ARRAY);
  const [createdPosting, setCreatedPosting] = useState(null);

  const handleApproveClick = useCallback(() => {
    const errors = validateForApproval?.() ?? [];
    setValidationErrors(errors);
    setShowApproveModal(true);
  }, [validateForApproval]);

  const handleApproveConfirm = useCallback(
    async (value) => {
      if (value === MESSAGE.APPROVE) {
        if (validationErrors.length > 0) {
          return;
        }

        setIsSubmitting(true);
        setShowApproveModal(false);

        try {
          const postingData = buildPostingData?.();

          if (!postingData) {
            setIsSubmitting(false);
            return;
          }

          if (!createPosting) {
            throw new Error('Weekly GL posting service is not configured.');
          }
          const result = await createPosting(postingData).unwrap();
          const newPosting = result?.newPosting ?? result?.data?.newPosting ?? null;
          setCreatedPosting(newPosting);

          setIsSubmitting(false);
          setShowSuccessModal(true);
          onApprovalSuccess?.();
        } catch (error) {
          setFailureMessage(buildWeeklyGLErrorMessage(error));
          setIsSubmitting(false);
          setShowFailureModal(true);
        }
      } else {
        setShowApproveModal(false);
        setValidationErrors(DEFAULTS.EMPTY_ARRAY);
      }
    },
    [validationErrors, onApprovalSuccess, createPosting, buildPostingData],
  );

  const handleFailureModalAction = useCallback(
    (value) => {
      if (value === ACTION_VALUES.TRY_AGAIN) {
        setShowFailureModal(false);
        handleApproveClick();
      } else {
        setShowFailureModal(false);
      }
    },
    [handleApproveClick],
  );

  const handleCloseSuccessModal = useCallback(() => {
    setShowSuccessModal(false);
  }, []);

  return {
    isSubmitting,
    showApproveModal,
    showSuccessModal,
    showFailureModal,
    failureMessage,
    validationErrors,
    createdPosting,
    onApproveClick: handleApproveClick,
    onApproveConfirm: handleApproveConfirm,
    onFailureAction: handleFailureModalAction,
    onCloseSuccess: handleCloseSuccessModal,
  };
};
